
import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface PricePerSqMeterEvolutionChartProps {
  data: Array<{
    month: string;
    scrape_date: string;
    avg_price_per_sqm: number;
    total_properties: number;
    properties_with_area_util: number;
    properties_with_area_total: number;
  }>;
  isMobile?: boolean;
  dashboardType?: 'venda' | 'aluguel';
}

const PricePerSqMeterEvolutionChart: React.FC<PricePerSqMeterEvolutionChartProps> = ({ 
  data, 
  isMobile = false,
  dashboardType = 'venda'
}) => {
  const formatCurrency = (value: number) => {
    return `R$ ${value.toLocaleString('pt-BR')}`;
  };

  const formatMonth = (monthStr: string) => {
    const [year, month] = monthStr.split('-');
    const monthNames = [
      'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
      'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
    ];
    return `${monthNames[parseInt(month) - 1]}/${year.slice(-2)}`;
  };

  // Reverter a ordem dos dados para mostrar cronologicamente
  const sortedData = [...data].reverse().map(item => ({
    ...item,
    monthFormatted: formatMonth(item.month)
  }));

  // Definir marcadores do eixo Y a cada 250 reais de 4750 a 5150
const getYAxisTicks = (dashboardType: 'venda' | 'aluguel') => {
  if (dashboardType === 'venda') {
    return [4750, 4850, 4950, 5050, 5150]; // Intervalos para venda
  } else {
    return [25, 30, 35, 40]; // Intervalos para aluguel (exemplo: R$/m² para aluguel)
  }
};
const yAxisTicks = getYAxisTicks(dashboardType);

const getYDomain = (dashboardType: 'venda' | 'aluguel') => {
  if (dashboardType === 'venda') {
    return [4750, 5150]; // Domain para venda
  } else {
    return [20, 45]; // Domain para aluguel (ajustado para cobrir os ticks)
  }
};
  
const yDomain = getYDomain(dashboardType);

  if (sortedData.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        Dados insuficientes para exibir o gráfico
      </div>
    );
  }




  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={sortedData}
        margin={isMobile ? { top: 5, right: 10, left: 10, bottom: 5 } : { top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e6e9f0" opacity={0.5} />
        <XAxis 
          dataKey="monthFormatted" 
          stroke="#2f4f4f"
          tick={{ fontSize: isMobile ? 10 : 12 }}
          tickMargin={isMobile ? 5 : 10}
        />
        <YAxis 
          domain={yDomain}
          ticks={yAxisTicks}
          tickFormatter={formatCurrency}
          stroke="#2f4f4f"
          tick={{ fontSize: isMobile ? 10 : 12 }}
          width={isMobile ? 70 : 90}
          tickMargin={isMobile ? 2 : 5}
        />
        <Tooltip 
          formatter={(value: number) => [formatCurrency(value), "Preço Médio/m²"]}
          labelFormatter={(label) => `Mês: ${label}`}
          contentStyle={isMobile ? { fontSize: '12px', padding: '4px' } : {}}
        />
        <Line 
          type="monotone" 
          dataKey="avg_price_per_sqm" 
          name="Preço Médio/m²"
          stroke="#1e90ff"
          strokeWidth={2}
          dot={{ r: isMobile ? 3 : 4, fill: "#1e90ff", strokeWidth: 1 }}
          activeDot={{ r: isMobile ? 5 : 6 }}
          animationDuration={1500} 
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default PricePerSqMeterEvolutionChart;
